<template>
  <div>
    <div class="modal fade" tabindex="-1" ref="modalEl">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ isWhatsAppPackage ? 'Pagar pacote de envios' : 'Pagar fatura' }}</h5>
            <button type="button" class="btn-close" @click="close"></button>
          </div>
          <div class="modal-body">
            <p class="mb-1"><strong>Referência:</strong> {{ charge?.charge_reference }}</p>
            <p class="mb-1" v-if="charge?.package?.label">
              <strong>Pacote:</strong> {{ charge.package.label }}
            </p>
            <p class="mb-3"><strong>Valor:</strong> R$ {{ formatMoney(charge?.charge_amount) }}</p>

            <label class="form-label">Forma de pagamento</label>
            <select v-model="paymentMethod" class="form-select mb-3">
              <option value="pix">PIX</option>
              <option v-if="!isWhatsAppPackage" value="bank_slip">Boleto</option>
              <option v-if="showCreditCardOption" value="credit_card">
                {{ hasSavedCard ? 'Cartão cadastrado' : 'Cartão de crédito' }}
              </option>
            </select>

            <p v-if="paymentMethod === 'credit_card' && hasSavedCard" class="text-muted fs-7 mb-0">
              A cobrança será feita no cartão já cadastrado para este local.
            </p>

            <PlatformSubscriptionCardForm
              v-if="paymentMethod === 'credit_card' && !hasSavedCard"
              ref="cardForm"
              :loading="loading"
              submit-label="Pagar com cartão"
              @submit="pay"
            />
          </div>
          <div class="modal-footer" v-if="paymentMethod !== 'credit_card' || hasSavedCard">
            <button type="button" class="btn btn-light" @click="close">Cancelar</button>
            <button type="button" class="btn btn-primary" :disabled="loading" @click="pay">
              {{ paymentMethod === 'pix' ? 'Gerar PIX' : 'Confirmar pagamento' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <PixPaymentModal
      ref="pixModal"
      :pix-data="pixData"
      @paymentConfirmed="onPixPaymentConfirmed"
      @onClose="pixData = null"
    />
  </div>
</template>

<script>
import PlatformSubscriptionCardForm from './PlatformSubscriptionCardForm.vue';
import PixPaymentModal from '../../components/PixPaymentModal.vue';
import http from '../../helpers/http';
import Alert from '../../helpers/alert';

export default {
  name: 'PlatformSubscriptionPayChargeModal',
  components: { PlatformSubscriptionCardForm, PixPaymentModal },
  props: {
    charge: { type: Object, default: null },
    defaultPaymentMethod: { type: String, default: 'pix' },
    hasSavedCard: { type: Boolean, default: false },
  },
  emits: ['paid', 'close'],
  data() {
    return {
      modal: null,
      paymentMethod: 'pix',
      loading: false,
      pixData: null,
      chargePollInterval: null,
    };
  },
  watch: {
    charge(value) {
      if (value) {
        let method = value.payment_method || this.defaultPaymentMethod || 'pix';
        if (value.charge_kind === 'whatsapp_package' && method === 'bank_slip') {
          method = 'pix';
        }
        this.paymentMethod = method;
        this.open();
      }
    },
  },
  computed: {
    isWhatsAppPackage() {
      return this.charge?.charge_kind === 'whatsapp_package';
    },
    showCreditCardOption() {
      return this.isWhatsAppPackage || this.hasSavedCard;
    },
  },
  mounted() {
    this.modal = bootstrap.Modal.getOrCreateInstance(this.$refs.modalEl);
    this.$refs.modalEl.addEventListener('hidden.bs.modal', () => this.$emit('close'));
  },
  beforeUnmount() {
    this.stopChargePolling();
  },
  methods: {
    open() {
      this.modal?.show();
    },
    close() {
      this.modal?.hide();
    },
    formatMoney(value) {
      return Number(value || 0).toFixed(2).replace('.', ',');
    },
    pay() {
      if (!this.charge || this.loading) {
        return;
      }

      this.loading = true;
      const placeId = this.$store.state.auth.place.id_place;
      const payload = { payment_method: this.paymentMethod };

      if (this.paymentMethod === 'credit_card' && this.$refs.cardForm) {
        Object.assign(payload, this.$refs.cardForm.payload());
      }

      http.post(
        `/places/${placeId}/platform-subscription/charges/${this.charge.id_place_platform_charge}/pay`,
        payload,
      ).then((res) => {
        const data = res.data.data || res.data;
        this.handlePaymentResponse(data, placeId);
      }).catch((error) => {
        const message = error.response?.data?.message
          || error.response?.data?.errors?.payment_method?.[0]
          || 'Não foi possível iniciar o pagamento.';
        Alert.error(message);
      }).finally(() => {
        this.loading = false;
      });
    },
    handlePaymentResponse(data, placeId) {
      if (data.pix_copy_paste || data.pix_qr_code) {
        this.pixData = {
          pix_code: data.pix_copy_paste,
          qr_code_base64: data.pix_qr_code,
          platform_charge_id: data.id_place_platform_charge,
          place_id: placeId,
          expires_at: data.charge_due_date ? `${data.charge_due_date}T23:59:59` : null,
        };
        this.close();
        this.$nextTick(() => {
          this.$refs.pixModal?.open();
        });
        return;
      }

      if (data.boleto_url) {
        window.open(data.boleto_url, '_blank');
        Alert.success('Boleto gerado.');
        this.close();
        return;
      }

      if (data.charge_status === 'paid') {
        Alert.success('Pagamento confirmado!');
        this.close();
        this.$emit('paid');
        return;
      }

      if (this.paymentMethod === 'credit_card') {
        this.close();
        this.startChargePolling(placeId, data.id_place_platform_charge);
        Alert.success('Cobrança enviada ao cartão. Aguardando confirmação...');
        return;
      }

      Alert.success('Pagamento iniciado. Aguarde a confirmação.');
      this.close();
    },
    onPixPaymentConfirmed() {
      this.pixData = null;
      this.$emit('paid');
    },
    startChargePolling(placeId, chargeId) {
      this.stopChargePolling();

      this.chargePollInterval = setInterval(() => {
        http.get(`/places/${placeId}/platform-subscription/charges/${chargeId}`)
          .then((res) => {
            const charge = res.data?.data || res.data;
            if (charge?.charge_status === 'paid') {
              this.stopChargePolling();
              Alert.success('Pagamento confirmado!');
              this.$emit('paid');
            }
          })
          .catch(() => {});
      }, 2000);
    },
    stopChargePolling() {
      if (this.chargePollInterval) {
        clearInterval(this.chargePollInterval);
        this.chargePollInterval = null;
      }
    },
  },
};
</script>
